/** The headline band: how much of Microsoft 365 is actually working right now. */

import { Card, StatisticBar } from "@norican/react";

import type { M365HealthSummary } from "@/lib/api";

import { type StripTone, SUMMARY_LABELS } from "./status";

interface SummaryStripProps {
  summary: M365HealthSummary;
  resolvedWindowDays: number;
}

export function SummaryStrip({ summary, resolvedWindowDays }: SummaryStripProps) {
  const allOperational =
    summary.services_total > 0 && summary.services_operational === summary.services_total;

  const servicesTone: StripTone = allOperational
    ? "positive"
    : summary.services_interrupted > 0
      ? "negative"
      : "caution";

  return (
    <Card variant="subtle">
      <Card.Body>
        <StatisticBar
          aria-label="Microsoft 365 service health summary"
          size="lg"
          items={[
            {
              label: SUMMARY_LABELS.servicesOperational,
              value: `${summary.services_operational} of ${summary.services_total}`,
              tone: servicesTone,
              caption: allOperational
                ? "Full tenant health"
                : `${summary.services_degraded} degraded · ${summary.services_interrupted} interrupted`,
            },
            {
              label: SUMMARY_LABELS.activeIncidents,
              value: String(summary.active_incidents),
              tone: summary.active_incidents > 0 ? "negative" : "positive",
              caption: summary.active_incidents > 0 ? "Users are affected" : "None open",
            },
            {
              label: SUMMARY_LABELS.activeAdvisories,
              value: String(summary.active_advisories),
              tone: summary.active_advisories > 0 ? "caution" : "positive",
              caption: "Lower-impact notices",
            },
            {
              label: SUMMARY_LABELS.noStatus,
              value: String(summary.services_unknown),
              caption: "Graph reported nothing",
            },
            {
              label: SUMMARY_LABELS.resolved(resolvedWindowDays),
              value: String(summary.resolved_last_7_days),
              tone: "accent",
              align: "end",
            },
          ]}
        />
      </Card.Body>
    </Card>
  );
}
